import React, { useState, useMemo } from 'react';
import { GitCompare, ArrowLeftRight, Trash2, FileText, Copy, Check } from 'lucide-react';

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
  leftNum: number | null;
  rightNum: number | null;
}

// LCS-based diff
function computeDiff(original: string, modified: string): DiffLine[] {
  const origLines = original.split('\n');
  const modLines = modified.split('\n');
  const m = origLines.length;
  const n = modLines.length;

  // Build LCS table
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (origLines[i - 1] === modLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff
  const result: DiffLine[] = [];
  let i = m, j = n;
  const temp: DiffLine[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && origLines[i - 1] === modLines[j - 1]) {
      temp.push({ type: 'unchanged', text: origLines[i - 1], leftNum: i, rightNum: j });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      temp.push({ type: 'added', text: modLines[j - 1], leftNum: null, rightNum: j });
      j--;
    } else {
      temp.push({ type: 'removed', text: origLines[i - 1], leftNum: i, rightNum: null });
      i--;
    }
  }

  // Reverse since we built it backwards
  for (let k = temp.length - 1; k >= 0; k--) {
    result.push(temp[k]);
  }

  return result;
}

const SAMPLE_ORIGINAL = `The quick brown fox
jumps over the lazy dog.
This line stays the same.
Remove this line.
Another shared line.
Old content here.`;

const SAMPLE_MODIFIED = `The quick brown fox
leaps over the lazy dog.
This line stays the same.
Another shared line.
New content here.
An added line at the end.`;

const TextDiffCompare: React.FC = () => {
  const [original, setOriginal] = useState('');
  const [modified, setModified] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [copied, setCopied] = useState(false);

  const diff = useMemo(() => {
    if (!showDiff || (!original && !modified)) return [];
    return computeDiff(original, modified);
  }, [original, modified, showDiff]);

  const stats = useMemo(() => {
    const added = diff.filter(d => d.type === 'added').length;
    const removed = diff.filter(d => d.type === 'removed').length;
    const unchanged = diff.filter(d => d.type === 'unchanged').length;
    return { added, removed, unchanged, total: added + removed };
  }, [diff]);

  const swap = () => {
    const temp = original;
    setOriginal(modified);
    setModified(temp);
    if (showDiff) setShowDiff(true);
  };

  const clearBoth = () => {
    setOriginal('');
    setModified('');
    setShowDiff(false);
  };

  const loadSample = () => {
    setOriginal(SAMPLE_ORIGINAL);
    setModified(SAMPLE_MODIFIED);
    setShowDiff(false);
  };

  const copyDiff = () => {
    const text = diff.map(d => {
      const prefix = d.type === 'added' ? '+ ' : d.type === 'removed' ? '- ' : '  ';
      return prefix + d.text;
    }).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-orange-100 rounded-lg">
          <GitCompare className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Text Diff Compare</h1>
          <p className="text-gray-500 text-sm">Compare two texts and see the differences</p>
        </div>
      </div>

      {/* Input textareas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Original</label>
          <textarea
            value={original}
            onChange={(e) => { setOriginal(e.target.value); setShowDiff(false); }}
            placeholder="Paste the original text here..."
            className="w-full h-48 p-3 border border-gray-300 rounded-lg resize-y text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Modified</label>
          <textarea
            value={modified}
            onChange={(e) => { setModified(e.target.value); setShowDiff(false); }}
            placeholder="Paste the modified text here..."
            className="w-full h-48 p-3 border border-gray-300 rounded-lg resize-y text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setShowDiff(true)}
          disabled={!original && !modified}
          className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
        >
          <GitCompare className="w-4 h-4" /> Compare
        </button>
        <button onClick={swap} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4" /> Swap
        </button>
        <button onClick={clearBoth} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-2">
          <Trash2 className="w-4 h-4" /> Clear Both
        </button>
        <button onClick={loadSample} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-2">
          <FileText className="w-4 h-4" /> Sample Texts
        </button>
      </div>

      {/* Diff output */}
      {showDiff && diff.length > 0 && (
        <>
          {/* Stats bar */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full border border-green-200">
              +{stats.added} added
            </span>
            <span className="px-3 py-1 bg-red-50 text-red-700 rounded-full border border-red-200">
              -{stats.removed} removed
            </span>
            <span className="px-3 py-1 bg-gray-50 text-gray-600 rounded-full border border-gray-200">
              {stats.unchanged} unchanged
            </span>
            <span className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full border border-orange-200">
              {stats.total} total changes
            </span>
            <button onClick={copyDiff} className="ml-auto text-gray-500 hover:text-gray-700 flex items-center gap-1">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              <span className="text-xs">{copied ? 'Copied!' : 'Copy diff'}</span>
            </button>
          </div>

          {/* Diff lines */}
          <div className="border border-gray-200 rounded-lg overflow-hidden font-mono text-sm">
            {diff.map((line, i) => (
              <div
                key={i}
                className={`flex border-b border-gray-100 last:border-b-0 ${
                  line.type === 'added'
                    ? 'bg-green-50'
                    : line.type === 'removed'
                    ? 'bg-red-50'
                    : 'bg-white'
                }`}
              >
                {/* Line numbers */}
                <div className="w-12 flex-shrink-0 text-right pr-2 py-1 text-gray-400 bg-gray-50 border-r border-gray-200 select-none">
                  {line.leftNum ?? ''}
                </div>
                <div className="w-12 flex-shrink-0 text-right pr-2 py-1 text-gray-400 bg-gray-50 border-r border-gray-200 select-none">
                  {line.rightNum ?? ''}
                </div>
                {/* Prefix */}
                <div className={`w-6 flex-shrink-0 text-center py-1 font-bold ${
                  line.type === 'added' ? 'text-green-600' : line.type === 'removed' ? 'text-red-600' : 'text-gray-300'
                }`}>
                  {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                </div>
                {/* Content */}
                <div className={`flex-1 py-1 px-2 whitespace-pre-wrap break-all ${
                  line.type === 'added'
                    ? 'text-green-800'
                    : line.type === 'removed'
                    ? 'text-red-800'
                    : 'text-gray-700'
                }`}>
                  {line.text || '\u00A0'}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showDiff && diff.length === 0 && original === '' && modified === '' && (
        <p className="text-gray-400 text-sm text-center py-8">Enter text in both fields and click Compare.</p>
      )}
    </div>
  );
};

export default TextDiffCompare;
